import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, EventEmitter, Input, Output } from '@angular/core';
import { NotificationCategories } from '@core/domain/model/notifications/notification.model';
import { AcRoutingService } from '@core/services/routing/ac-routing.service';
import { NotificationsListComponent } from '@shared/components/notifications-list/notifications-list.component';
import { OptionsMenuComponent } from '@shared/ui-components/options-menu/options-menu.component';
import { ButtonComponent } from '@shared/ui-components/button/button.component';
import { IconComponent } from '@shared/ui-components/icon/icon.component';
import { LoadingPanelComponent } from '@shared/ui-components/loading-panel/loading-panel.component';
import { notificationsActions } from '@store/actions/notifications.actions';
import { notificationsSelectors } from '@store/selectors/notifications.selectors';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { appActions } from '@store/actions/app.actions';
import { appSelectors } from '@store/selectors/app.selectors';
import { LoadingIds } from '@shared/constants/loading';
import { OptionMenu } from '@shared/ui-components/options-menu/options-menu.model';

@Component({
  selector: 'ac-notifications-drawer',
  standalone: true,
  imports: [
    OptionsMenuComponent,
    IconComponent,
    ButtonComponent,
    NgClass,
    NotificationsListComponent,
    LoadingPanelComponent,
    TranslateModule,
  ],
  templateUrl: './notifications-drawer.component.html',
  styleUrl: './notifications-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsDrawerComponent {
  _show: boolean = false;
  @Input({ required: true }) set show(value: boolean) {
    this._show = value;
    if (value) {
      this._store.dispatch(appActions.disableScroll());
      this._store.dispatch(notificationsActions.setCategory({ category: this._category }));
    } else {
      this._store.dispatch(appActions.enableScroll());
    }
  }
  @Output() closed = new EventEmitter();

  protected readonly _headerMenuOptionsSig = computed(() => {
    const canRead = this._store.selectSignal(notificationsSelectors.canRead)();
    const canArchive = this._store.selectSignal(notificationsSelectors.canArchive)();
    if (!canRead && !canArchive) {
      return null;
    }
    const options: OptionMenu<string>[] = [];
    if (canRead) {
      options.push({
        id: 'mark-all-as-read',
        label: 'NOTIFICATIONS.MARK_ALL_AS_READ',
        action: () => this._store.dispatch(notificationsActions.readAll()),
      });
    }
    if (canArchive) {
      options.push({
        id: 'archive-all',
        label: 'NOTIFICATIONS.ARCHIVE_ALL',
        action: () => this._store.dispatch(notificationsActions.archiveAll()),
      });
    }
    return options;
  });

  protected readonly _category = NotificationCategories.ALL;

  /** STORE SIGNALS */
  protected readonly _notificationsSig = computed(() => this._store.selectSignal(notificationsSelectors.notifications)().slice(0, 15));
  protected readonly _loadingSig = computed(() =>
    this._store.selectSignal(appSelectors.specificLoading)().includes(LoadingIds.NOTIFICATIONS_PANEL)
  );
  protected readonly _unreadCountSig = this._store.selectSignal(notificationsSelectors.unreadCounter);

  constructor(
    private readonly _routingService: AcRoutingService,
    private readonly _store: Store<Store>
  ) {}

  onClose() {
    this.closed.emit();
  }

  onClickViewAll() {
    this.onClose();
    this._routingService.goToNotificationsSelector();
  }
}
